import { useMemo, useState } from 'react';
import { CHART_VIEWS } from '@/features/ai-agents/chat/MarkdownTableVisualization/constants';
import {
  ParsedTable,
  TableSort,
  TableViewType,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import {
  findDefaultXAxisColumnIndex,
  getCurrentColumnOrder,
  getNextSort,
  sortTableRows,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

interface UseVisualizationStateOptions {
  chartNumericColumnIndexes: number[];
  defaultXColumnIndex: number;
  isVariantReport: boolean;
  table: ParsedTable;
  variantColumnIndex: number;
}

export function useVisualizationState({
  chartNumericColumnIndexes,
  defaultXColumnIndex,
  isVariantReport,
  table,
  variantColumnIndex,
}: UseVisualizationStateOptions) {
  const [view, setView] = useState<TableViewType>(() =>
    isVariantReport ? `line` : `table`,
  );
  const [columnOrder, setColumnOrder] = useState(() =>
    table.headers.map((_header, index) => index),
  );
  const [sort, setSort] = useState<TableSort | null>(null);
  const [xColumnIndex, setXColumnIndex] = useState(defaultXColumnIndex);
  const [yColumnIndex, setYColumnIndex] = useState(
    chartNumericColumnIndexes.find((index) => index !== defaultXColumnIndex) ??
      chartNumericColumnIndexes[0] ??
      0,
  );
  const orderedColumnIndexes = getCurrentColumnOrder(
    columnOrder,
    table.headers.length,
  );
  const availableXColumnIndexes = orderedColumnIndexes.filter(
    (columnIndex) => columnIndex !== variantColumnIndex,
  );
  const selectedXColumnIndex = availableXColumnIndexes.includes(xColumnIndex)
    ? xColumnIndex
    : findDefaultXAxisColumnIndex(
        table.headers.length,
        chartNumericColumnIndexes,
        variantColumnIndex,
      );
  const selectedYColumnIndex = chartNumericColumnIndexes.includes(yColumnIndex)
    ? yColumnIndex
    : chartNumericColumnIndexes.find(
        (index) => index !== selectedXColumnIndex,
      ) ??
      chartNumericColumnIndexes[0] ??
      0;
  const currentSort = table.headers[sort?.columnIndex] ? sort : null;
  const sortedRows = useMemo(
    () =>
      sortTableRows(
        table.rows,
        currentSort?.columnIndex,
        currentSort?.direction,
      ),
    [table.rows, currentSort?.columnIndex, currentSort?.direction],
  );
  const canShowChart = chartNumericColumnIndexes.length > 0;
  const activeView = isVariantReport && view !== `table` ? `line` : view;
  const availableChartViews = isVariantReport
    ? CHART_VIEWS.filter((chartView) => chartView.value === `line`)
    : CHART_VIEWS;

  const handleSort = (columnIndex: number) => {
    setSort((currentSort) => getNextSort(currentSort, columnIndex));
  };

  return {
    activeView,
    availableChartViews,
    availableXColumnIndexes,
    canShowChart,
    currentSort,
    handleSort,
    orderedColumnIndexes,
    selectedXColumnIndex,
    selectedYColumnIndex,
    setColumnOrder,
    setView,
    setXColumnIndex,
    setYColumnIndex,
    sortedRows,
  };
}
